"use strict";

import axios from "axios";
import {retryStaleSession} from "../utils/stale-session-retry";
import store from "../state/store";
import router from "../router";
import { sessionEnded, markSessionEnded, clearSessionEnded } from "@/utils/session-state";

// Full config:  https://github.com/axios/axios#request-config
// axios.defaults.baseURL = process.env.baseURL || process.env.apiUrl || '';
// axios.defaults.headers.common['Authorization'] = AUTH_TOKEN;
// axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';

function objToFormData (obj = {}, formData = new FormData(), key = '') {
    if (!([Array, File, Object].includes(obj.constructor))) {
        return formData;
    }

    // Handle File recursive
    if (obj instanceof File) {
        formData.append(key, obj);
        return formData;
    }

    const bool = (value) => typeof value === 'boolean' ? (value === true ? 1 : 0) : value;

    for (const prop in obj) {
        // Validate value type
        if (obj[prop] && !([String, Number, Boolean, Array, Object, File].includes(obj[prop].constructor))) {
            continue;
        }

        // Set deep index of prop
        const deepKey = key ? key + `[${prop}]` : prop;

        // Handle array
        if (Array.isArray(obj[prop])) {
            if (obj[prop].length) {
                obj[prop].forEach((item, index) => {
                    if (typeof item === 'object') {
                        objToFormData(item, formData, `${deepKey}[${index}]`);
                    } else {
                        formData.append(deepKey + `[${index}]`, bool(item))
                    }
                });
                continue;
            } else {
                // formData.append(`${deepKey}[0]`, '')
                // continue;
            }
        }

        (typeof obj[prop] === 'object' && obj[prop] && obj[prop].constructor === Object)
            ? objToFormData(obj[prop], formData, deepKey) // Handle object
            : formData.append(deepKey, [undefined, null].includes(obj[prop]) ? '' : bool(obj[prop])) // Handle string, number, boolean
    }

    return formData;
}

const apiUrl = window.__env.api.url;

let config = {
    baseURL: apiUrl || process.env.baseURL || process.env.apiUrl || '',
    transformRequest: [function (data) {
        // return (data instanceof FormData ? data : objToFormData(data));
        if (this.method !== 'patch') {
            return (data instanceof FormData ? data : objToFormData(data));
        } else {
            let arrData = [];

            for (let prop in data) {
                arrData.push(prop + '=' + encodeURIComponent(data[prop]));
            }

            return arrData.join('&');
        }
    }],
    // timeout: 60 * 1000, // Timeout
    withCredentials: true, // Check cross-site Access-Control
};

// lejárt munkamenet: a háttérkérések egyszerre kapják a 401-et — egy átirányítás menjen, és a következő
// belépésig a védett kérések el se induljanak (különben mind újabb 401 és újabb kiléptetés lenne)
let sessionExpiredRedirecting = false;
const PUBLIC_REQUEST = /^\/?(api\/)?(login|logout|forgot-password|reset-password|two-factor|sanctum\/|csrf-cookie|translation|public\/)/;
const isPublicRequest = (url) => PUBLIC_REQUEST.test(String(url || '').replace(/^https?:\/\/[^/]+/, ''));

const _axios = axios.create(config);

_axios.interceptors.request.use(
    function(config) {
        if (sessionEnded() && !isPublicRequest(config.url)) {
            return Promise.reject(new axios.Cancel('session ended'));
        }

        // Runtime (config/config.js: window.__env.spa.id) felülírhatja a build-time
        // app-azonosítót — így ugyanaz a build más-más applikációként futhat
        // domainenként (tenant-kötött appok); fallback a beégetett env. Szándékosan
        // || (nem ??): üres/falsy spa.id félrekonfiguráció → a default app-ra esünk.
        config.headers['X-SPA-ID'] = window.__env?.spa?.id || process.env.VUE_APP_CLIENT_ID;
        // Do something before request is sent
        config.headers.Accept = 'application/json'
        if (localStorage.getItem('accessToken')) {
            config.headers.Authorization = 'Bearer ' + localStorage.getItem('accessToken')
        }
        return config;
    },
    function(error) {
        // Do something with request error
        return Promise.reject(error);
    }
);

// Add a response interceptor
_axios.interceptors.response.use(
    function(response) {
        // Do something with response data
        if (response.data && response.data.access_token) {
            localStorage.setItem('accessToken', response.data.access_token)
            clearSessionEnded();
        }
        return response;
    },
    function(error) {
        // Munkamenet-csere közben elkésett kérés (belépés, SSO, átjelentkezés): a szerver
        // `X-Session-Stale`-lel jelzi, hogy nem kijelentkezés. Újraküldjük, nem léptetünk ki.
        const staleRetry = retryStaleSession(_axios, error);
        if (staleRetry) {
            return staleRetry;
        }

        // PUBLIKUS oldalon (ahol nincs bejelentkezés) a 401/419 nem jelent lejárt munkamenetet:
        // ott nincs is felhasználó, és több háttérkérés (pl. a lusta fordítás-lekérés) eleve
        // auth-védett. Ilyenkor nem léptetünk ki és nem dobunk a loginra — különben a meghívó-
        // linkes kitöltő oldal azonnal a bejelentkezésre vinné a pácienst.
        const onPublicPage = router.currentRoute.value.matched.some(m => m.meta && m.meta.publicPage);

        if (! onPublicPage && error.response && [401, 419].includes(error.response.status)) {
            store.dispatch('auth/logoutOnlyFrontend')

            if (router.currentRoute.value?.name !== 'login' && !sessionExpiredRedirecting) {
                sessionExpiredRedirecting = true;
                markSessionEnded();
                setTimeout(() => { sessionExpiredRedirecting = false; }, 5000);
                router.push({ name: 'login' })
            }
        }

        // Do something with response error
        return Promise.reject(error);
    }
);

export default _axios;
